"use client";

import { useState } from "react";
import { toast } from "react-toastify";
import { useTranslations } from "next-intl";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const MAX_LENGTH = 2000;

/**
 * « Proposer une fonctionnalité », dans la barre latérale : un bouton (icône
 * seule quand la barre est repliée) et sa fenêtre. La proposition part par
 * e-mail à l'équipe (/api/interface/feature-suggestion).
 *
 * Monté dans le layout racine : ses textes vivent dans le namespace « layout ».
 */
export default function FeatureSuggestion({ compact = false }) {
    const t = useTranslations("layout");
    const [open, setOpen] = useState(false);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);

    const send = async () => {
        if (sending) return;
        if (!text.trim()) {
            toast.error(t("FeatureSuggestion_empty"));
            return;
        }
        setSending(true);
        try {
            const response = await fetch("/api/interface/feature-suggestion", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ suggestion: text, page: window.location.pathname }),
            });
            if (!response.ok) throw new Error();
            setText("");
            setOpen(false);
            toast.success(t("FeatureSuggestion_sent"));
        } catch {
            toast.error(t("FeatureSuggestion_error"));
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            {compact ? (
                <div className="relative group w-full">
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        aria-label={t("FeatureSuggestion_button")}
                        className="sidenav-link sidenav-link-compact w-full"
                    >
                        <SolarIcon name="lightbulb-linear" className="w-5 h-5" />
                    </button>
                    <span
                        role="tooltip"
                        className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 rounded-md bg-gray-900 dark:bg-grayDark-700 text-white text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50 shadow-lg"
                    >
                        {t("FeatureSuggestion_button")}
                    </span>
                </div>
            ) : (
                // Un bouton, pas un lien de navigation : posé sous la liste des
                // liens, il passait pour le suivant (signalé sur téléphone par
                // LN le 29/10/2026). D'où la pilule bordée, plus petite.
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="flex w-full items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-borderDark dark:bg-bgDark-400 dark:text-grayDark-100 dark:hover:bg-bgDark-300"
                >
                    <SolarIcon name="lightbulb-linear" className="w-4 h-4 shrink-0" />
                    <span>{t("FeatureSuggestion_button")}</span>
                </button>
            )}

            <BottomSheetModal
                isOpen={open}
                onClose={() => !sending && setOpen(false)}
                maxWidthClass="sm:max-w-lg"
                zIndexClass="z-[9999]"
                labelledBy="feature-suggestion-title"
                portal
            >
                <div className="p-6">
                    <h3 id="feature-suggestion-title" className="mb-1 text-lg font-bold text-slate-900 dark:text-white">
                        {t("FeatureSuggestion_title")}
                    </h3>
                    <p className="mb-5 text-sm text-slate-500 dark:text-grayDark-400">
                        {t("FeatureSuggestion_description")}
                    </p>
                    <textarea
                        rows={6}
                        value={text}
                        maxLength={MAX_LENGTH}
                        autoFocus
                        onChange={(event) => setText(event.target.value)}
                        aria-labelledby="feature-suggestion-title"
                        placeholder={t("FeatureSuggestion_placeholder")}
                        className="block w-full resize-none rounded-2xl border-0 bg-slate-100 px-4 py-3 text-sm leading-relaxed text-slate-900 outline-hidden placeholder:text-slate-400 focus:ring-2 focus:ring-slate-900/10 dark:bg-white/10 dark:text-white dark:placeholder:text-grayDark-500 dark:focus:ring-white/20"
                    />
                    <div className="mt-6 flex items-center justify-end gap-2 pb-1">
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            disabled={sending}
                            className="cursor-pointer rounded-full px-4 py-2.5 text-sm font-medium text-slate-600 transition-colors duration-150 hover:bg-slate-100 disabled:opacity-50 dark:text-grayDark-300 dark:hover:bg-white/10"
                        >
                            {t("FeatureSuggestion_cancel")}
                        </button>
                        <Button
                            small
                            isLoading={sending}
                            onClick={send}
                            className="!rounded-full !bg-primary !text-white !font-medium !text-sm hover:!bg-primary-300"
                        >
                            {t("FeatureSuggestion_send")}
                        </Button>
                    </div>
                </div>
            </BottomSheetModal>
        </>
    );
}
